import clsx from 'clsx';
import find from 'lodash/find';
import flattenDeep from 'lodash/flattenDeep';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Image, MediaInfoFragment, MediaType, useGetShowroomSectionsQuery } from 'generated/graphql';

import { getImageUrl, getVideoPreviewUrl } from 'lib/cloudinary';
import { useIsShowroomMobileVersion } from 'lib/showroom';

import Popper from '@material-ui/core/Popper';
import Typography from '@material-ui/core/Typography';

import { ReactComponent as EmptyBasketMediaActionsIcon } from 'icons/emptyBasketMediaActions.svg';

import { LANDSCAPE_WIDTH, PORTRAIT_WIDTH, useStyles } from './styles';

const ShowroomSamplesEmptyBasket = ({ id }) => {
	const { data } = useGetShowroomSectionsQuery({ variables: { id } });
	const sections = React.useMemo(() => data?.getShowroom.sections || [], [data]);

	const classes = useStyles();
	const { t } = useTranslation();

	const mobileVersion = useIsShowroomMobileVersion();

	const [anchor, setAnchor] = React.useState<HTMLElement>(null);
	const handleAnchorRef = React.useCallback(node => setAnchor(node), []);

	const sampleMedia = React.useMemo(() => {
		const allMedias: MediaInfoFragment[] = flattenDeep(sections.map(section => section.rows.map(row => row.medias)));
		return find(allMedias, media => media.isSample);
	}, [sections]);

	const isLandscape = React.useCallback(
		() => sampleMedia.mediaType === MediaType.Video || (sampleMedia as Image).width > (sampleMedia as Image).height,
		[sampleMedia]
	);

	const url = React.useMemo(() => {
		if (!sampleMedia) return '';
		const version = sampleMedia.cloudinaryVersion + '';
		const imgWidth = isLandscape() ? LANDSCAPE_WIDTH : PORTRAIT_WIDTH;
		if (sampleMedia.mediaType === MediaType.Video)
			return getVideoPreviewUrl(sampleMedia.cloudinaryId, { version, width: imgWidth, crop: 'scale' });
		else return getImageUrl(sampleMedia.cloudinaryId, { version, width: imgWidth, crop: 'scale' });
	}, [sampleMedia, isLandscape]);

	return (
		<div className={clsx(classes.root, { [classes.rootMobile]: mobileVersion })}>
			<div className={clsx(classes.mediaColumn, { [classes.mediaColumnMobile]: mobileVersion })}>
				{sampleMedia ? (
					<div
						className={clsx(classes.mediaColumnInner, {
							[classes.mediaColumnLandscapeInner]: isLandscape(),
							[classes.mediaColumnPortraitInner]: !isLandscape()
						})}
					>
						<img onContextMenu={ev => ev.preventDefault()} className={classes.mediaImg} src={url} alt="" />
						<div className={classes.mediaActionsContainer}>
							<EmptyBasketMediaActionsIcon ref={handleAnchorRef} />
						</div>
						<Popper open anchorEl={anchor} disablePortal placement="bottom" className={classes.popper}>
							<div className={classes.popperContent}>{t('showroom.samples.list.add')}</div>
						</Popper>
					</div>
				) : null}
			</div>
			<div className={clsx(classes.textColumn, { [classes.textColumnMobile]: mobileVersion })}>
				<div className={clsx(classes.textColumnInner, { [classes.textColumnInnerMobile]: mobileVersion })}>
					<Typography component="h3" className={clsx(classes.title, { [classes.titleMobile]: mobileVersion })}>
						{t('showroom.samples.list.empty')}
					</Typography>
					<div className={classes.textWrapper}>{t('showroom.samples.list.emptyText')}</div>
				</div>
			</div>
		</div>
	);
};

export default React.memo(ShowroomSamplesEmptyBasket);
